import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  FaShoppingCart,
  FaTimes,
  FaCrown,
  FaCoins,
  FaGem,
  FaBox,
  FaPlus,
  FaMinus,
  FaSpinner,
  FaCreditCard,
  FaCheck,
} from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { usePlayer } from '../../context/PlayerContext';
import { useTranslation } from '../../i18n';
import { storeAPI, tebexAPI, coinAPI } from '../../services/api';
import styles from './CartSidebar.module.scss';

const CartSidebar: React.FC = () => {
  const {
    items,
    totalItems,
    totalCoins,
    totalDollars,
    removeFromCart,
    updateQuantity,
    isCartOpen,
    closeCart,
    isCheckoutOpen,
    openCheckout,
    closeCheckout,
    clearCart,
  } = useCart();
  const { user, refreshUser } = useAuth();
  const { coinBalance, refreshBalance } = usePlayer();
  const { t } = useTranslation();

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const hasCoinItems = totalCoins > 0;
  const hasTebexItems = totalDollars > 0;

  const handleCoinPurchase = useCallback(async () => {
    if (!user || coinBalance < totalCoins) return;

    const coinItems = items.filter(item => item.type === 'rank' || (item.type !== 'coins'));
    if (coinItems.length === 0) return;

    for (const item of coinItems) {
      if (item.type === 'rank') {
        const parts = item.id.split('-');
        const durationPart = parts.pop();
        const slug = parts.join('-');
        const duration = parseInt(durationPart?.replace('m', '') || '1');
        await storeAPI.purchaseRank(slug, duration);
      } else {
        await coinAPI.spend(item.price * item.quantity, item.id, item.name);
      }
    }
  }, [user, coinBalance, totalCoins, items]);

  const handleTebexPayment = useCallback(async () => {
    const cartItems = items
      .filter(item => item.type === 'coins')
      .map(item => ({
        productId: item.id,
        quantity: item.quantity,
      }));

    if (cartItems.length === 0) return;

    const response = await tebexAPI.createCheckout(cartItems);
    const { checkoutUrl } = response.data;

    if (checkoutUrl) {
      const url = new URL(checkoutUrl);
      if (url.hostname.endsWith('.tebex.io') || url.hostname === 'tebex.io') {
        window.location.href = checkoutUrl;
      } else {
        throw new Error('Invalid checkout URL');
      }
    } else {
      throw new Error('Failed to get checkout URL');
    }
  }, [items]);

  const handleConfirmPurchase = useCallback(async () => {
    setPaymentLoading(true);
    setPaymentError(null);

    try {
      // Process coin-paid items first (ranks, cosmetics)
      if (hasCoinItems) {
        await handleCoinPurchase();
      }

      // Then process real-money items via Tebex
      if (hasTebexItems) {
        await handleTebexPayment();
        // Tebex redirects away, so we don't reach here
        return;
      }

      // If only coin purchases, show success
      setPaymentSuccess(true);
      clearCart();
      refreshUser();
      refreshBalance();

      setTimeout(() => {
        closeCheckout();
        setPaymentSuccess(false);
      }, 2000);
    } catch (err: any) {
      setPaymentError(err.response?.data?.error || err.message || 'Purchase failed');
    } finally {
      setPaymentLoading(false);
    }
  }, [hasCoinItems, hasTebexItems, handleCoinPurchase, handleTebexPayment, clearCart, refreshUser, refreshBalance, closeCheckout]);

  return ReactDOM.createPortal(
    <>
      {/* Cart Overlay */}
      <div
        className={`${styles['cart-overlay']} ${isCartOpen ? styles.open : ''}`}
        onClick={closeCart}
      />

      {/* Cart Sidebar */}
      <div className={`${styles['cart-sidebar']} ${isCartOpen ? styles.open : ''}`}>
        <div className={styles['cart-header']}>
          <h3>
            <FaShoppingCart /> {t('store.cart', { count: totalItems.toString() })}
          </h3>
          <button className={styles['close-btn']} onClick={closeCart}>
            <FaTimes />
          </button>
        </div>

        <div className={styles['cart-items']}>
          {items.length === 0 ? (
            <div className={styles['empty-cart']}>
              <FaShoppingCart />
              <p>{t('store.cartEmpty')}</p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className={styles['cart-item']}>
                <div className={styles['item-image']}>
                  {item.type === 'rank' && <FaCrown />}
                  {item.type === 'coins' && <FaCoins />}
                  {item.type === 'cosmetic' && <FaGem />}
                </div>
                <div className={styles['item-details']}>
                  <h4>{item.name}</h4>
                  <span className={styles.price}>
                    {item.type === 'coins' ? `$${item.price}` : `${item.price} coins`}
                  </span>
                </div>
                <div className={styles['item-actions']}>
                  <div className={styles.quantity}>
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>
                      <FaMinus />
                    </button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                      <FaPlus />
                    </button>
                  </div>
                  <button
                    className={styles['remove-btn']}
                    onClick={() => removeFromCart(item.id)}
                  >
                    {t('store.remove')}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className={styles['cart-footer']}>
            <div className={styles['cart-totals']}>
              {totalCoins > 0 && (
                <div className={styles['cart-total-row']}>
                  <span><FaCoins /> {t('store.coinsLabel')}</span>
                  <span className={styles['total-coins']}>{totalCoins.toLocaleString()} coins</span>
                </div>
              )}
              {totalDollars > 0 && (
                <div className={styles['cart-total-row']}>
                  <span>💵 {t('store.realMoney')}</span>
                  <span className={styles['total-dollars']}>${totalDollars.toFixed(2)}</span>
                </div>
              )}
            </div>
            <button className={styles['checkout-btn']} onClick={openCheckout}>
              {t('store.checkout')}
            </button>
          </div>
        )}
      </div>

      {/* Checkout Modal */}
      {isCheckoutOpen && (
        <>
          <div className={styles['checkout-overlay']} onClick={closeCheckout} />
          <div className={styles['checkout-modal']}>
            <div className={styles['checkout-header']}>
              <h2>{t('store.checkoutTitle')}</h2>
              <button className={styles['close-btn']} onClick={closeCheckout}>
                <FaTimes />
              </button>
            </div>

            <div className={styles['checkout-body']}>
              <div className={styles['checkout-section']}>
                <h3>{t('store.orderSummary')}</h3>
                <div className={styles['checkout-items']}>
                  {items.map((item) => (
                    <div key={item.id} className={styles['checkout-item']}>
                      <div className={styles['checkout-item-icon']}>
                        {item.type === 'rank' && <FaCrown />}
                        {item.type === 'coins' && <FaCoins />}
                        {item.type === 'cosmetic' && <FaGem />}
                        {item.type === 'crate' && <FaBox />}
                      </div>
                      <div className={styles['checkout-item-info']}>
                        <span className={styles['checkout-item-name']}>{item.name}</span>
                        <span className={styles['checkout-item-qty']}>{t('store.qty')} {item.quantity}</span>
                      </div>
                      <span className={styles['checkout-item-price']}>
                        {item.type === 'coins' ? `$${(item.price * item.quantity).toFixed(2)}` : `${(item.price * item.quantity).toLocaleString()} coins`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles['checkout-section']}>
                <h3>{t('store.paymentDetails')}</h3>
                <div className={styles['checkout-totals']}>
                  {totalCoins > 0 && (
                    <div className={styles['checkout-total-row']}>
                      <span><FaCoins /> {t('store.inGameCoins')}</span>
                      <span className={styles['coins-value']}>{totalCoins.toLocaleString()} coins</span>
                    </div>
                  )}
                  {totalDollars > 0 && (
                    <div className={styles['checkout-total-row']}>
                      <span>💵 {t('store.realMoney')}</span>
                      <span className={styles['dollars-value']}>${totalDollars.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {user && (
                <div className={styles['checkout-section']}>
                  <h3>{t('store.accountInfo')}</h3>
                  <div className={styles['checkout-user-info']}>
                    <div className={styles['user-info-row']}>
                      <span>{t('store.username')}</span>
                      <span>{user.username}</span>
                    </div>
                    <div className={styles['user-info-row']}>
                      <span>{t('store.currentBalance')}</span>
                      <span>{coinBalance.toLocaleString()} coins</span>
                    </div>
                    <div className={styles['user-info-row']}>
                      <span>{t('store.billing')}</span>
                      <span>{user.billingPreference === 'invoice' ? t('store.invoice') : t('store.receipt')}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles['checkout-section']}>
                <h3>{t('store.selectPayment')}</h3>

                {paymentError && (
                  <div className={styles['payment-error']}>
                    {paymentError}
                  </div>
                )}

                {paymentSuccess && (
                  <div className={styles['payment-success']}>
                    <FaCheck /> {t('payment.success')}
                  </div>
                )}

                <div className={styles['payment-methods']}>
                  {hasCoinItems && (
                    <div className={`${styles['payment-method-btn']} ${styles.active}`}>
                      <FaCoins className={styles['payment-icon']} />
                      <div className={styles['payment-info']}>
                        <span className={styles['payment-name']}>{t('store.payWithCoins')}</span>
                        <span className={styles['payment-desc']}>
                          {coinBalance >= totalCoins
                            ? t('store.useCoins', { amount: totalCoins.toLocaleString() })
                            : t('store.insufficientBalance', { balance: coinBalance.toLocaleString() })
                          }
                        </span>
                      </div>
                    </div>
                  )}

                  {hasTebexItems && (
                    <div className={`${styles['payment-method-btn']} ${styles.tebex} ${styles.active}`}>
                      <FaCreditCard className={styles['payment-icon']} />
                      <div className={styles['payment-info']}>
                        <span className={styles['payment-name']}>{t('store.cardPaypal')}</span>
                        <span className={styles['payment-desc']}>{t('store.paySecurely', { amount: totalDollars.toFixed(2) })}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles['checkout-footer']}>
              <button
                className={styles['cancel-btn']}
                onClick={() => {
                  closeCheckout();
                  setPaymentError(null);
                }}
                disabled={paymentLoading}
              >
                {t('store.cancel')}
              </button>
              <button
                className={styles['confirm-checkout-btn']}
                onClick={handleConfirmPurchase}
                disabled={paymentLoading || (hasCoinItems && coinBalance < totalCoins)}
              >
                {paymentLoading ? (
                  <>
                    <FaSpinner className={styles.spinner} /> {t('store.processing')}
                  </>
                ) : (
                  t('store.confirmPurchase')
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </>,
    document.body
  );
};

export default CartSidebar;
